// Rules:
// - Entries hold **static copy only**. Server/exception text goes in `ModAlert`'s
//   `children` slot at the call site, not into `description` here.
// - Field names match `ModAlert` (`title`, `description`) so entries can be spread:
//     <ModAlert intent="error" {...ERROR_MESSAGES.global.unknown}>
//       {getDetailedErrorMessage(error)}
//     </ModAlert>
// - Action language ("Try again.") stays copy-side; the Retry button (when
//   `ModerneGraphQLError` is given a `refetch`) is the actual retry affordance.
// - TODO(verify)  — asserts system behavior; owner must confirm before shipping.
// - TODO(migrate) — target call site that will read from this entry in Phase 3.
// - TODO(plurals) — `(s)` placeholder pending a plural() helper.

export type ErrorEntry = { title: string; description?: string };

type ErrorSection = Record<
  string,
  ErrorEntry | ((...args: never[]) => ErrorEntry)
>;

export const ERROR_MESSAGES = {
  global: {
    unreachable: {
      title: 'Moderne Platform unreachable.',
      // TODO(verify): does any code path auto-reconnect? Drop "temporarily" if not.
      // TODO(migrate): graphql-error.component.tsx:80 (isUnavailableError).
      description: 'The API is temporarily unavailable. Try again.'
    },
    unauthorized: {
      title: 'Unauthorized.',
      // TODO(migrate): graphql-error.component.tsx:76,110.
      description: "You aren't authorized to perform this action."
    },
    notFound: {
      title: 'Not found.',
      // TODO(migrate): graphql-error.component.tsx:82,114.
      description: "The requested resource couldn't be found."
    },
    networkError: {
      title: 'Network error.',
      // TODO(migrate): graphql-error.component.tsx:98,121.
      description:
        "We couldn't reach the services needed to load this. Check your connection and refresh."
    },
    dataIntegrity: {
      title: 'Incomplete data returned.',
      // TODO(verify): is this ever transient, or always a backend bug? If always
      // a backend bug, drop the "Try again" and route straight to support.
      // TODO(migrate): graphql-error.component.tsx:78,112.
      description:
        'A backend issue caused required data to be missing. Try again, or contact support if it keeps happening.'
    },
    noConnectedConnector: (feature: string): ErrorEntry => ({
      title: `${feature} failed to load.`,
      // TODO(migrate): graphql-error.component.tsx:100,123.
      // Follow-up: extend backend to populate error.extensions.connector so we
      // can name the specific connector here.
      description:
        "A required Moderne Connector isn't connected. Ask a Moderne admin to enable it."
    }),
    unknown: {
      title: 'Request failed.',
      // Server's error message is rendered via `children` (see file header).
      // TODO(migrate): graphql-error.component.tsx:102,125; also chat/results generic fallbacks.
      description:
        'The cause is unknown. Try again, or contact support if it persists.'
    },
    rateLimited: {
      title: 'Rate limit exceeded.',
      // TODO(verify): is "a few minutes" the actual backoff, or arbitrary?
      // TODO(migrate): apollo-client.helper.ts:156-161 (currently ships this string inline).
      description: 'Too many requests. Try again in a few minutes.'
    },
    globalOrgNotSupported: (feature: string): ErrorEntry => ({
      title: `${feature} isn't available on the Global organization.`,
      // TODO(migrate): global-org-not-supported.component.tsx:13 — drop inline copy, keep SVG + action.
      description:
        'The Global organization is a read-only aggregate view. Select a specific organization to use this feature.'
    })
  },

  errorPages: {
    notFound: {
      title: 'Page not found.',
      description:
        'It may have been moved or deleted, or the URL may be incorrect.'
    },
    serverError: {
      title: 'Something went wrong on our end.',
      description:
        "We're experiencing a technical issue. Contact support if it persists."
    },
    authError: {
      title: 'Sign-in failed.',
      description: 'Try signing in again. Contact support if it persists.'
    }
  },

  chat: {
    LLM_OVERLOADED: {
      title: 'Model provider overloaded.',
      // TODO(verify): is "in a moment" backed by any retry?
      description:
        'The model provider is currently overloaded. Try again in a moment.'
    },
    LLM_RATE_LIMITED: {
      title: 'Model provider rate-limited.',
      // TODO(verify): same question as LLM_OVERLOADED.
      description:
        'The model provider is rate-limiting requests. Try again in a moment.'
    },
    LLM_AUTH_FAILED: {
      title: 'Model provider rejected credentials.',
      description:
        'An administrator needs to update the model provider configuration.'
    },
    LLM_CONTEXT_TOO_LONG: {
      title: 'Conversation too long.',
      description:
        'This conversation has grown too long for the model. Start a new conversation to continue.'
    },
    LLM_TIMED_OUT: {
      title: "Model provider didn't respond.",
      description: 'Try again.'
    },
    LLM_QUOTA_EXCEEDED: {
      title: 'Model provider quota exhausted.',
      description:
        'An administrator needs to review the model provider billing.'
    },
    LLM_UNREACHABLE: {
      title: 'Model provider unreachable.',
      description: 'Check network connectivity and try again.'
    },
    LLM_UNAVAILABLE: {
      title: 'Model provider unavailable.',
      description: 'The model provider is temporarily unavailable. Try again.'
    },
    LLM_EMPTY_RESPONSE: {
      title: 'Empty response from model.',
      description: 'Try rephrasing your request.'
    },
    LLM_FAILED: {
      title: 'Model request failed.',
      description: 'Something went wrong with the model request. Try again.'
    },
    TURN_LIMIT_EXCEEDED: {
      title: 'Tool-use limit reached.',
      description:
        'This turn exceeded the tool-use limit. Break your request into smaller steps.'
    },
    TOOL_FAILED: {
      title: 'Tool invocation failed.',
      description: 'See details for the underlying cause.'
    },
    TOOL_UNKNOWN: {
      title: 'Requested tool unavailable.',
      description:
        "The model asked for a tool that isn't available. Try rephrasing your request."
    },
    CANCELLED: {
      title: 'Turn cancelled.',
      description: 'The conversation turn was cancelled.'
    },
    INTERNAL: {
      title: 'Something went wrong on our end.',
      // TODO(verify): is INTERNAL transient? Add a timing hint only if so.
      description: 'Try again.'
    },
    // Fallback when content.code doesn't match any known key. `ErrorMessage.code`
    // is `Maybe<String>` in the schema, so backend can add new codes without our
    // knowing — this keeps the UI from rendering blank.
    unknown: {
      title: 'Chat error.',
      description:
        'The chat encountered an unrecognized error. Try again, or contact support.'
    },
    inProgress: {
      title: 'Still working on your request.',
      // TODO(verify): fires on state === 'IN_PROGRESS'; confirm processing is actually continuing.
      description: 'Please wait while the model finishes.'
    },
    assistantStopped: {
      title: 'Assistant stopped responding.',
      description: 'Send your message again.'
    }
  },

  results: {
    activityNotFound: {
      title: 'Activity not found.',
      description:
        'This activity may have been deleted, or the ID is incorrect.'
    },
    runCompletedWithErrors: {
      title: 'Recipe run ended with errors.',
      // TODO(verify): does the UI actually show both errors and valid results
      // together, and can the valid ones be committed independently?
      description:
        'The recipe ran into issues with some repositories. The results below completed and can be committed.'
    },
    runError: {
      title: 'Recipe run failed.',
      description:
        'An error occurred while running this recipe. Check the Data tables tab for details.'
    },
    repoErrorLstLoad: { title: 'LST loading failed.' },
    repoErrorRecipeLoad: { title: 'Recipe loading failed.' },
    repoErrorRecipe: {
      title: 'Recipe error.',
      // Per-file RECIPE_ERRORs carry no message; without one the State cell's
      // popover never opens.
      description:
        'This recipe errored on one or more files. See the "Source files that errored on a recipe" data table for details.'
    },
    repoErrorTimeout: { title: 'Timeout.' },
    filterFailed: {
      title: "Couldn't apply filter.",
      description: 'Showing the last loaded results. Try the filter again.'
    },
    resultsPending: {
      title: 'Still processing this repository.',
      description: 'This recipe run has not reached this repository yet.'
    },
    resultsUnread: {
      title: 'Results are ready.',
      description: 'This repository finished after the page loaded.'
    },
    emptyNoResults: { title: 'No results for this recipe run' },
    emptyNoErrorResults: {
      title: 'No errors found',
      description: "You're only viewing results with errors."
    },
    emptyNoErrors: { title: 'No errors in this recipe run.' },
    emptyNoDataTables: { title: 'No data tables produced.' },
    emptyNoMatchingDataTables: { title: 'No tables match this filter.' },
    // Distinct from emptyNoDataTables: a failed lookup must not read as a run
    // that produced nothing.
    dataTablesLoadFailed: {
      title: 'Could not load data tables.',
      description:
        'Something went wrong fetching the data tables for this run. Reload to try again.'
    },
    agentTeamLoadFailed: {
      title: 'Could not load the agent team.',
      description:
        'Something went wrong fetching the agents working on this changeset. Reload to try again.'
    },
    recipeRepositoriesLoadFailed: {
      title: 'Could not load the recipe repositories.',
      description: 'Reload to try again.'
    },
    boardMessagesLoadFailed: {
      title: 'Could not load the message board.',
      description:
        "Something went wrong fetching the agents' messages. Reload to try again."
    },
    resultsUnavailable: { title: 'Results are no longer available.' },
    repoDetailsDefault: { title: 'Select a repository to view details.' },
    repoAccessDenied: { title: "You don't have access to this repository." }
  },

  mcp: {
    serverUnavailable: {
      title: 'MCP server unavailable.',
      description: "The MCP server isn't reachable right now. Check back later."
    },
    toolExecutionFailed: { title: 'Tool execution failed.' }
  },

  activity: {
    loadFailed: {
      title: 'There was an error retrieving activity.',
      description: 'Reload to try again.'
    }
  },

  organizations: {
    selectRepository: {
      title: 'Select at least one repository.',
      description: 'Choose one or more repositories before saving.'
    },
    organizationNotFound: {
      title: 'Organization not found.',
      description: "This organization doesn't exist."
    }
  },

  builder: {
    recipeNotFoundInMarketplace: { title: 'Recipe not found in marketplace.' },
    recipeNotFound: (recipeId: string): ErrorEntry => ({
      title: 'Recipe not found.',
      description: `Recipe \`${recipeId}\` couldn't be found.`
    }),
    // Fires only when the duplicate recipe has zero options
    // (`n.val.options?.length === 0`). A recipe *with* options can legitimately
    // appear twice, so "has no options" is load-bearing.
    duplicateRecipe: (recipeId: string): ErrorEntry => ({
      title: `Recipe \`${recipeId}\` has no options and is already part of your custom recipe.`,
      description: 'Use search and filter to find where it currently appears.'
    }),
    recipesDeleted: (count: number): ErrorEntry => ({
      // TODO(plurals): swap `(s)` for a plural() helper once introduced.
      title: `${count} recipe(s) deleted.`
    }),
    noDevCenterRecipes: {
      title: 'No DevCenter recipes available.',
      description:
        "Your admin hasn't loaded any DevCenter recipe bundles. To configure a DevCenter, first add a recipe jar to your environment."
    }
  },

  visualizations: {
    mermaidParseFailed: {
      title: "Diagram couldn't be rendered.",
      description:
        'The diagram source is invalid. See details for the parser output.'
    },
    visualizationLoadFailed: { title: 'Visualization failed to load.' },
    visualizationNoData: { title: 'No data.' },
    visualizationDataUnavailable: {
      title: 'Visualization data unavailable.',
      description:
        "The data required for this visualization isn't available right now."
    },
    dataTablesUnavailable: {
      title: 'No data tables available.',
      description: 'Data tables are only available for completed recipe runs.'
    },
    dataTablesNoneProduced: { title: 'No data tables produced.' },
    dataTablesNoneMatchFilter: { title: 'No data tables match this filter.' }
  },

  commit: {
    repositoriesLoadFailed: {
      title: 'Could not load repositories.',
      description:
        'Something went wrong fetching the repositories for this job. Reload to try again.'
    },
    noRepositoriesSelected: {
      title: 'No repositories selected.',
      description: 'Select at least one repository to commit.'
    },
    pullRequestStatusUnavailable: {
      title: 'Unavailable',
      description:
        'Pull request status could not be loaded from the source control provider.'
    },
    recipeErrorsPresent: {
      title: 'Recipe errors detected.',
      description:
        "At least one selected repository has recipe errors. Errors won't be committed, but the changes may be incomplete."
    },
    noChangesToCommit: { title: 'No changes to commit' },
    exclusionsUnreadable: {
      title:
        "Couldn't read which files you unchecked, so committing all results could include them"
    },
    exclusionsLoading: { title: 'Loading which files you unchecked' },
    selectionWaitsForRun: {
      title:
        'Checked organizations include repositories not loaded yet; commit them once the run finishes'
    }
  },

  scmAuth: {
    connectSuccess: { title: 'SCM connected.' },
    authorizeFailed: {
      title: 'Unable to authorize.',
      description: "We couldn't authorize with your SCM provider. Try again."
    },
    disconnectConfirm: (origin: string): ErrorEntry => ({
      title: 'Disconnect SCM.',
      description: `Disconnect from ${origin}?`
    }),
    disconnectSuccess: (origin: string): ErrorEntry => ({
      title: `Disconnected from ${origin}.`
    }),
    // Server's `revokeScmToken.error` takes precedence via `children` when present
    // (see file header).
    disconnectFailed: {
      title: 'Unable to disconnect.',
      description: 'Failed to disconnect.'
    },
    notConfiguredAdmin: {
      title: 'SCM not configured.',
      description:
        'Connect the connector to your SCM so Moderne can display recipe results and commit changes back to it.'
    },
    notConfiguredUser: {
      title: 'SCM not configured.',
      description: 'Ask your Moderne admin to configure an SCM connection.'
    },
    authRequired: (origin: string): ErrorEntry => ({
      title: 'SCM authorization required.',
      description: `Connect to ${origin} to view recipe results for this repository.`
    }),
    popupBlocked: {
      title: 'Popup blocked.',
      description: 'Allow popups for this site and try again.'
    },
    authTimedOut: {
      title: 'Authorization timed out.',
      description: 'Try again.'
    },
    authCancelled: {
      title: 'Authorization cancelled.',
      description: 'Try again.'
    },
    storageUnavailable: {
      title: 'Browser storage unavailable.',
      description: 'Enable site data for this site and try again.'
    }
  },

  fileDiff: {
    binaryFile: { title: 'Binary file not shown.' },
    whitespaceOnly: {
      title: 'Whitespace-only changes.',
      description: 'The only changes to this file were whitespace changes.'
    },
    diffUnavailable: {
      title: 'Unable to retrieve changes.',
      description:
        'The source may have failed to parse, we may have been unable to retrieve the diff, or the recipe may have failed on this file.'
    },
    recipeExecutionError: { title: 'Recipe execution error.' },
    diffTooLarge: {
      title: 'Diff too large.',
      description:
        'This diff is too large to submit with an issue. Copy the diff and paste it into the issue after choosing an option below.'
    }
  },

  recipes: {
    detailsLoadFailed: { title: 'Recipe details failed to load.' },
    noneFound: { title: 'No recipes found.' },
    searchNoneFound: { title: 'No results found.' },
    yamlParseFailed: { title: 'Unable to parse YAML.' },
    attributionUnavailableChange: {
      title: "Recipe attribution isn't available for this change."
    },
    attributionUnavailableResult: {
      title: "Recipe attribution isn't available for this result."
    },
    installTimedOut: {
      title: 'Recipe installation timed out.',
      description: 'Try again.'
    },
    bundlesNoneInstalled: {
      title: 'No recipe bundles installed.',
      description: 'Contact an administrator to install recipe bundles.'
    },
    bundlesNoneInstalledAdmin: {
      title: 'No recipe bundles installed.',
      description:
        'Load the default OpenRewrite recipes to get started, or use the Add button to install specific recipe bundles.'
    },
    // Claims nothing about install counts: the overlay only knows a filter is
    // on, so it must not promise there is more to see (#8586).
    bundlesNoneMatchFilter: {
      title: 'No recipe bundles match your filters.',
      description: 'Try a different search, or clear your filters.'
    }
  },

  changelog: {
    loadFailed: {
      title: 'Could not load the changelog.',
      description:
        'Something went wrong fetching changelog entries. Reload to try again.'
    },
    notEnabled: {
      title: "Changesets aren't enabled for this organization.",
      description:
        'An organization admin needs to enable and configure Changesets before data will appear here.'
    },
    noData: {
      title: 'No Changeset data available.',
      // TODO(verify): is ingestion ever in flight, or is this a permanent empty state?
      description:
        'No Changesets have been generated for this organization yet.'
    },
    noneMatchFilter: { title: 'No results match your filters.' }
  },

  settings: {
    tokenCreateFailed: {
      title: 'Unable to create token.',
      description:
        'Try again, or contact your administrator if the problem persists.'
    }
  },

  downloads: {
    initiateFailed: { title: 'Failed to initiate download.' },
    // Fallback when the server reports failure without a message.
    failed: { title: 'Download failed.' },
    jobNotFound: { title: 'Download job could not be found.' },
    timedOut: { title: 'Download timed out.' },
    sweepTimedOut: {
      title: 'The saved search was still running when the download gave up.'
    }
  }
} satisfies Record<string, ErrorSection>;
